import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  isMarcoPermissionError,
  type MarcoAssessment,
  type MarcoAssessmentsData,
} from "./useMarcoAssessments";

type MarcoAssessmentSectionProps = {
  data: MarcoAssessmentsData | undefined;
  isPending: boolean;
  isError: boolean;
  error?: unknown;
};

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const Field = ({
  label,
  value,
  note,
}: {
  label: string;
  value: string | null | undefined;
  note?: string | null;
}) => {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span>{value}</span>
      {note && <span className="text-muted-foreground text-xs">{note}</span>}
    </div>
  );
};

const PendingBadge = () => (
  <Badge
    variant="outline"
    className="border-amber-500 text-amber-700 dark:text-amber-400"
  >
    PENDING — not approved
  </Badge>
);

const ApprovedBadge = () => <Badge variant="secondary">Founder-approved</Badge>;

/** Approved content: plain priority, because it is the approved assessment. */
const ApprovedAssessment = ({ row }: { row: MarcoAssessment }) => (
  <div className="flex flex-col gap-2 rounded-md border p-3">
    <div className="flex flex-wrap items-center gap-2">
      <ApprovedBadge />
      <Badge variant="outline">{row.commercial_priority}</Badge>
    </div>
    <Field label="Priority rationale" value={row.priority_rationale} />
    <Field label="ICP fit" value={row.icp_fit} note={row.icp_fit_note} />
    <Field
      label="Buying intent"
      value={row.buying_intent}
      note={row.buying_intent_note}
    />
    <Field
      label="Potential value"
      value={row.potential_value}
      note={row.potential_value_note}
    />
    <Field label="Recommended offer" value={row.recommended_offer} />
    <Field label="Outreach angle" value={row.outreach_angle} />
    <Field
      label="Approved"
      value={
        row.approved_by || row.approved_at
          ? [row.approved_by, formatDate(row.approved_at)]
              .filter(Boolean)
              .join(" · ")
          : null
      }
      note={row.approval_evidence}
    />
    <Provenance row={row} />
  </div>
);

/**
 * Pending content: never labelled as approved. The priority reads
 * "Proposed P2", and the outreach angle is marked as not approved, with no send.
 */
const PendingAssessment = ({ row }: { row: MarcoAssessment }) => (
  <div className="flex flex-col gap-2 rounded-md border border-dashed border-amber-500/60 p-3">
    <div className="flex flex-wrap items-center gap-2">
      <PendingBadge />
      <Badge variant="outline">Proposed {row.commercial_priority}</Badge>
    </div>
    <Field label="Priority rationale" value={row.priority_rationale} />
    {row.relationship_priority && (
      <Field
        label="Relationship priority (proposed)"
        value={row.relationship_priority}
      />
    )}
    <Field
      label="ICP fit (proposed)"
      value={row.icp_fit}
      note={row.icp_fit_note}
    />
    <Field
      label="Buying intent (proposed)"
      value={row.buying_intent}
      note={row.buying_intent_note}
    />
    <Field
      label="Potential value (proposed)"
      value={row.potential_value}
      note={row.potential_value_note}
    />
    <Field label="Recommended offer (proposed)" value={row.recommended_offer} />
    <Field
      label="Outreach angle (proposed, not approved, no send)"
      value={row.outreach_angle}
    />
    {row.decision_evidence && (
      <Field
        label="Founder decision reference (not an approval of this assessment)"
        value={row.decision_evidence}
      />
    )}
    <Provenance row={row} />
  </div>
);

const Provenance = ({ row }: { row: MarcoAssessment }) => (
  <p className="text-muted-foreground text-xs">
    Assessed {formatDate(row.assessed_on) ?? row.assessed_on}
    {row.source_note_id != null && ` · source note #${row.source_note_id}`}
    {` · run ${row.run_id} · ${row.mode}`}
  </p>
);

/**
 * Renders Marco's assessments from the two assessment views. Each state is
 * distinct: loading, permission or query error, no assessment (renders
 * nothing, so the legacy empty state applies), pending only, approved only,
 * and both (approved first, pending below).
 */
export const MarcoAssessmentSection = ({
  data,
  isPending,
  isError,
  error,
}: MarcoAssessmentSectionProps) => {
  if (isPending) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true">
        <span className="text-muted-foreground text-xs">
          Loading assessment…
        </span>
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-destructive text-xs">
        {isMarcoPermissionError(error)
          ? "You don't have permission to view Marco assessments."
          : "Couldn't load Marco's assessments."}
      </p>
    );
  }

  if (!data || (!data.approved && data.pending.length === 0)) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {data.approved && <ApprovedAssessment row={data.approved} />}
      {data.pending.map((row) => (
        <PendingAssessment key={row.id} row={row} />
      ))}
    </div>
  );
};
